import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Inbox, Archive, ShieldAlert, TrendingUp, Search, Download,
  LogOut, Trash2, RotateCcw, Mail, Phone, X,
} from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import api, { API, TOKEN_KEY } from "../../lib/api";
import { Wordmark } from "../../components/Logo";

const STATUS_TABS = [
  { key: "", label: "All", icon: Inbox },
  { key: "new", label: "New", icon: Mail },
  { key: "archived", label: "Archived", icon: Archive },
  { key: "spam", label: "Spam", icon: ShieldAlert },
];

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="rounded-xl border border-[#262626] bg-[#111111] p-6">
    <div className="flex items-center justify-between">
      <span className="text-sm text-white/50">{label}</span>
      <Icon size={18} className="text-gold" />
    </div>
    <p className="mt-3 font-display text-4xl font-medium text-white" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{value}</p>
  </div>
);

const statusBadge = (s) => {
  const map = {
    new: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    archived: "bg-white/10 text-white/60 border-white/20",
    spam: "bg-red-500/15 text-red-300 border-red-500/30",
  };
  return map[s] || map.new;
};

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [enquiries, setEnquiries] = useState([]);
  const [analytics, setAnalytics] = useState({ total: 0, new: 0, archived: 0, spam: 0, last_7_days: 0 });
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/analytics");
      setAnalytics(data);
    } catch (e) { /* ignore */ }
  }, []);

  const loadEnquiries = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (status) params.status = status;
      if (search.trim()) params.search = search.trim();
      const { data } = await api.get("/admin/enquiries", { params });
      setEnquiries(data);
    } catch (e) { /* ignore */ } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => { loadAnalytics(); }, [loadAnalytics]);
  useEffect(() => {
    const t = setTimeout(loadEnquiries, 250);
    return () => clearTimeout(t);
  }, [loadEnquiries]);

  const updateStatus = async (id, newStatus) => {
    await api.patch(`/admin/enquiries/${id}`, { status: newStatus });
    await Promise.all([loadEnquiries(), loadAnalytics()]);
    setSelected(null);
  };

  const remove = async (id) => {
    if (!window.confirm("Permanently delete this enquiry?")) return;
    await api.delete(`/admin/enquiries/${id}`);
    await Promise.all([loadEnquiries(), loadAnalytics()]);
    setSelected(null);
  };

  const exportCsv = async () => {
    const token = localStorage.getItem(TOKEN_KEY);
    const url = `${API}/admin/enquiries/export${status ? `?status=${status}` : ""}`;
    const res = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
    const blob = await res.blob();
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "intolaw_enquiries.csv";
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const handleLogout = () => {
    logout();
    navigate("/admin/login", { replace: true });
  };

  const fmtDate = (iso) => {
    try { return new Date(iso).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="dark min-h-screen bg-[#050505] font-body text-white">
      <div className="flex">
        {/* Sidebar */}
        <aside className="fixed inset-y-0 left-0 hidden w-64 flex-col border-r border-[#262626] bg-[#0a0a0a] p-6 lg:flex">
          <Wordmark variant="light" className="text-2xl" />
          <p className="mt-1 text-[10px] uppercase tracking-[0.3em] text-white/30">Dashboard</p>
          <nav className="mt-10 space-y-1">
            {STATUS_TABS.map((t) => (
              <button
                key={t.key}
                onClick={() => setStatus(t.key)}
                data-testid={`filter-${t.label.toLowerCase()}`}
                className={`flex w-full items-center gap-3 rounded-lg px-4 py-3 text-sm transition-colors ${
                  status === t.key ? "bg-gold text-[#050505]" : "text-white/60 hover:bg-white/5 hover:text-white"
                }`}
              >
                <t.icon size={18} /> {t.label}
              </button>
            ))}
          </nav>
          <button
            onClick={handleLogout}
            data-testid="logout-button"
            className="mt-auto flex items-center gap-3 rounded-lg px-4 py-3 text-sm text-white/60 transition-colors hover:bg-white/5 hover:text-white"
          >
            <LogOut size={18} /> Log out
          </button>
        </aside>

        {/* Main */}
        <div className="flex-1 lg:ml-64">
          <header className="flex items-center justify-between border-b border-[#262626] px-6 py-5 lg:px-10">
            <div>
              <h1 className="font-display text-2xl font-medium">Enquiries</h1>
              <p className="text-sm text-white/40">Welcome back, {user?.name || "Admin"}</p>
            </div>
            <div className="flex items-center gap-3">
              <a
                href="/brand/intolaw-brand-assets.zip"
                download
                data-testid="brand-assets-download"
                className="inline-flex items-center gap-2 rounded-md border border-[#262626] px-4 py-2 text-sm font-medium text-white/70 transition-colors hover:bg-white/5 hover:text-white"
              >
                <Download size={16} /> Brand Assets
              </a>
              <button
                onClick={exportCsv}
                data-testid="export-csv-button"
                className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-[#050505] transition-colors hover:bg-goldDark"
              >
                <Download size={16} /> Export CSV
              </button>
            </div>
          </header>

          <main className="px-6 py-8 lg:px-10">
            {/* Analytics */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <StatCard icon={Inbox} label="Total" value={analytics.total} />
              <StatCard icon={Mail} label="New" value={analytics.new} />
              <StatCard icon={TrendingUp} label="Last 7 Days" value={analytics.last_7_days} />
              <StatCard icon={ShieldAlert} label="Spam" value={analytics.spam} />
            </div>

            {/* Mobile filters */}
            <div className="mt-8 flex flex-wrap gap-2 lg:hidden">
              {STATUS_TABS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setStatus(t.key)}
                  className={`rounded-full px-4 py-2 text-sm ${status === t.key ? "bg-gold text-[#050505]" : "border border-[#262626] text-white/60"}`}
                >
                  {t.label}
                </button>
              ))}
            </div>

            {/* Search */}
            <div className="mt-8 relative max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-white/30" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search name, email or message…"
                data-testid="search-input"
                className="w-full rounded-md border border-[#262626] bg-[#111111] py-3 pl-11 pr-4 text-white outline-none focus:ring-1 focus:ring-gold"
              />
            </div>

            {/* Table */}
            <div className="mt-6 overflow-hidden rounded-xl border border-[#262626] bg-[#111111]">
              {loading ? (
                <p className="p-8 text-center text-white/40">Loading…</p>
              ) : enquiries.length === 0 ? (
                <p className="p-8 text-center text-white/40" data-testid="empty-state">No enquiries found.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm" data-testid="enquiries-table">
                    <thead className="border-b border-[#262626] text-xs uppercase tracking-wider text-white/40">
                      <tr>
                        <th className="px-6 py-4">Name</th>
                        <th className="px-6 py-4">Contact</th>
                        <th className="px-6 py-4 hidden md:table-cell">Message</th>
                        <th className="px-6 py-4">Status</th>
                        <th className="px-6 py-4">Date</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#1c1c1c]">
                      {enquiries.map((e) => (
                        <tr
                          key={e.id}
                          onClick={() => setSelected(e)}
                          className="cursor-pointer transition-colors hover:bg-white/5"
                          data-testid={`enquiry-row-${e.id}`}
                        >
                          <td className="px-6 py-4 font-medium text-white">{e.first_name} {e.last_name}</td>
                          <td className="px-6 py-4 text-white/60">{e.email}</td>
                          <td className="px-6 py-4 hidden max-w-xs truncate text-white/50 md:table-cell">{e.message}</td>
                          <td className="px-6 py-4">
                            <span className={`rounded-full border px-2.5 py-1 text-xs capitalize ${statusBadge(e.status)}`}>{e.status}</span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-white/40">{fmtDate(e.created_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </main>
        </div>
      </div>

      {/* Detail drawer */}
      {selected && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="enquiry-detail">
          <div className="absolute inset-0 bg-black/60" onClick={() => setSelected(null)} />
          <div className="relative h-full w-full max-w-md overflow-y-auto border-l border-[#262626] bg-[#0a0a0a] p-8">
            <button onClick={() => setSelected(null)} className="absolute right-6 top-6 text-white/40 hover:text-white" data-testid="close-detail"><X size={20} /></button>
            <span className={`inline-block rounded-full border px-2.5 py-1 text-xs capitalize ${statusBadge(selected.status)}`}>{selected.status}</span>
            <h2 className="mt-4 font-display text-3xl font-medium">{selected.first_name} {selected.last_name}</h2>
            <div className="mt-6 space-y-4 text-sm">
              <p className="flex items-center gap-3 text-white/70"><Mail size={16} className="text-gold" /> {selected.email}</p>
              <p className="flex items-center gap-3 text-white/70"><Phone size={16} className="text-gold" /> {selected.phone}</p>
              <p className="text-white/40">{fmtDate(selected.created_at)} · {selected.country || "—"}</p>
            </div>
            <div className="mt-6 rounded-lg border border-[#262626] bg-[#111111] p-5">
              <p className="mb-2 text-xs uppercase tracking-wider text-white/40">Message</p>
              <p className="whitespace-pre-wrap leading-relaxed text-white/80">{selected.message}</p>
            </div>

            <div className="mt-8 flex flex-wrap gap-3">
              {selected.status !== "archived" && (
                <button onClick={() => updateStatus(selected.id, "archived")} data-testid="action-archive" className="inline-flex items-center gap-2 rounded-md border border-[#262626] px-4 py-2 text-sm text-white/70 hover:bg-white/5">
                  <Archive size={16} /> Archive
                </button>
              )}
              {selected.status !== "spam" ? (
                <button onClick={() => updateStatus(selected.id, "spam")} data-testid="action-spam" className="inline-flex items-center gap-2 rounded-md border border-red-500/30 px-4 py-2 text-sm text-red-300 hover:bg-red-500/10">
                  <ShieldAlert size={16} /> Mark Spam
                </button>
              ) : (
                <button onClick={() => updateStatus(selected.id, "new")} data-testid="action-restore" className="inline-flex items-center gap-2 rounded-md border border-[#262626] px-4 py-2 text-sm text-white/70 hover:bg-white/5">
                  <RotateCcw size={16} /> Restore
                </button>
              )}
              <button onClick={() => remove(selected.id)} data-testid="action-delete" className="inline-flex items-center gap-2 rounded-md border border-red-500/30 px-4 py-2 text-sm text-red-300 hover:bg-red-500/10">
                <Trash2 size={16} /> Delete
              </button>
              <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-medium text-[#050505] hover:bg-goldDark">
                <Mail size={16} /> Reply
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
